import { SORTS } from "./detailService";
import { supplierNotice } from "./deliverability";

export const DEFAULT_SORT = SORTS.DISTANCE;
export const MIN_RATING_THRESHOLD = 4;

export const INITIAL_PRODUCT_FILTERS = Object.freeze({
  sort: DEFAULT_SORT,
  minRating: null,
  maxDistanceKm: null,
  discountedOnly: false,
  includeOutOfStock: false,
});

export const countActiveProductFilters = (filters, bounds) => {
  const applied = filters || INITIAL_PRODUCT_FILTERS;

  let count = 0;

  if (applied.sort && applied.sort !== DEFAULT_SORT) count += 1;
  if (applied.minRating) count += 1;
  if (applied.discountedOnly) count += 1;
  if (applied.includeOutOfStock) count += 1;

  const ceiling = bounds?.maxDistanceKm;

  if (
    Number.isFinite(applied.maxDistanceKm) &&
    Number.isFinite(ceiling) &&
    applied.maxDistanceKm < ceiling
  ) {
    count += 1;
  }

  return count;
};

export const cheapestPerSupplier = (rows) => {
  const best = new Map();

  (rows || []).forEach((row) => {
    if (!row || row.supplierId === undefined || row.supplierId === null) return;
    if (!Number.isFinite(row.price) || row.price <= 0) return;

    const current = best.get(row.supplierId);

    if (!current || row.price < current.price) best.set(row.supplierId, row);
  });

  return Array.from(best.values());
};

export const resolveProductId = (rows) => {
  const cheapest = cheapestPerSupplier(rows).sort((a, b) => a.price - b.price);

  return cheapest.length ? cheapest[0].productId : null;
};

export const mergeOffers = ({ nearby = [], origin = null, offers = [] }) => {
  const merged = new Map();

  const take = (card) => {
    if (!card || card.supplierId === undefined || card.supplierId === null) return;
    if (merged.has(card.supplierId)) return;

    merged.set(card.supplierId, card);
  };

  nearby.forEach(take);
  take(origin);
  offers.forEach(take);

  return Array.from(merged.values());
};

export const decorateCards = (cards) =>
  (cards || []).map((card) => {
    const notice = supplierNotice(card);

    return {
      ...card,
      notice,
      deliverable: !notice,
      orderable: !notice && card.inStock !== false,
    };
  });

export const filterCards = (
  cards,
  { distanceCapKm, includeOutOfStock, discountedOnly, minRating }
) =>
  (cards || []).filter((card) => {
    if (Number.isFinite(distanceCapKm)) {
      if (!Number.isFinite(card.distanceKm)) return false;
      if (card.distanceKm > distanceCapKm) return false;
    }

    if (!includeOutOfStock && card.inStock === false) return false;
    if (discountedOnly && !card.discounted) return false;

    if (Number.isFinite(minRating) && !(card.rating >= minRating)) return false;

    return true;
  });

const byNumber = (a, b, direction = 1) => {
  const left = Number.isFinite(a) ? a : null;
  const right = Number.isFinite(b) ? b : null;

  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;

  return (left - right) * direction;
};

export const sortCards = (cards, sort) => {
  const rows = [...(cards || [])];

  if (sort === SORTS.PRICE_ASC) return rows.sort((a, b) => byNumber(a.price, b.price));
  if (sort === SORTS.PRICE_DESC) return rows.sort((a, b) => byNumber(b.price, a.price));
  if (sort === SORTS.RATING) return rows.sort((a, b) => byNumber(b.rating, a.rating));

  if (sort === SORTS.DISCOUNTED) {
    return rows.sort((a, b) => {
      if (a.discounted !== b.discounted) return a.discounted ? -1 : 1;

      return byNumber(a.price, b.price);
    });
  }

  return rows.sort((a, b) => byNumber(a.distanceKm, b.distanceKm));
};

export const orderableFirst = (cards) => {
  const rows = [...(cards || [])];

  return rows.sort((a, b) => {
    if (a.orderable === b.orderable) return 0;

    return a.orderable ? -1 : 1;
  });
};

export const rankCards = (cards) => {
  const rows = orderableFirst(cards);

  const pricedOrderable = rows.filter(
    (card) => card.orderable && Number.isFinite(card.price) && card.price > 0
  );

  const pool = pricedOrderable.length
    ? pricedOrderable
    : rows.filter((card) => Number.isFinite(card.price) && card.price > 0);

  if (!pool.length) return rows.map((card) => ({ ...card, highlighted: false }));

  const cheapest = Math.min(...pool.map((card) => card.price));
  const target = pool.find((card) => card.price === cheapest);

  return rows.map((card) => ({
    ...card,
    highlighted: card.key === target?.key,
  }));
};

export const buildFeed = (
  cards,
  { distanceCapKm, includeOutOfStock, discountedOnly, minRating, sort }
) =>
  rankCards(
    sortCards(
      filterCards(decorateCards(cards), {
        distanceCapKm,
        includeOutOfStock,
        discountedOnly,
        minRating,
      }),
      sort
    )
  );

export const distanceCap = (maxDistanceKm, radiusKm) => {
  if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
    return Number.isFinite(maxDistanceKm) ? maxDistanceKm : null;
  }

  if (!Number.isFinite(maxDistanceKm)) return radiusKm;

  return Math.min(maxDistanceKm, radiusKm);
};

export const distanceBoundKm = (radiusKm) =>
  Math.max(Math.ceil(Number.isFinite(radiusKm) ? radiusKm : 0), 1);
